import React, { useState, useEffect, useCallback } from 'react';

const DS_API = 'https://api.gp-pay.net';

function authHeaders() {
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('ds_token')}` };
}

function logout() {
    localStorage.removeItem('ds_token');
    localStorage.removeItem('ds_role');
    localStorage.removeItem('ds_username');
    localStorage.removeItem('ds_website_code');
    window.location.href = '/debit_security';
}

function fmtDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('bn-BD', { year: 'numeric', month: 'short', day: 'numeric' });
}

function isExpired(d) {
    if (!d) return false;
    return new Date(d) < new Date();
}

export default function DSAdminDashboard() {
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [activeCount, setActiveCount] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [msgType, setMsgType] = useState('');

    // Filters
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [page, setPage] = useState(1);
    const LIMIT = 20;

    // License edit modal
    const [licenseUser, setLicenseUser] = useState(null);
    const [licenseForm, setLicenseForm] = useState({ license_key: '', license_expiry: '' });
    const [savingLicense, setSavingLicense] = useState(false);

    const showMsg = (text, type = 'success') => {
        setMsg(text); setMsgType(type);
        setTimeout(() => setMsg(''), 3500);
    };

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ search, status, page, limit: LIMIT });
            const res = await fetch(`${DS_API}/debit_security/api/admin/users?${params}`, { headers: authHeaders() });
            if (res.status === 401) return logout();
            const data = await res.json();
            if (data.success) {
                setUsers(data.users);
                setTotal(data.total);
                setActiveCount(data.activeCount);
                setPendingCount(data.pendingCount);
            }
        } catch { showMsg('User লিস্ট লোড হয়নি', 'error'); }
        finally { setLoading(false); }
    }, [search, status, page]);

    useEffect(() => {
        if (localStorage.getItem('ds_role') !== 'ds_admin') { window.location.href = '/debit_security'; return; }
        loadUsers();
    }, [loadUsers]);

    const handleSearch = (e) => { e.preventDefault(); setPage(1); loadUsers(); };

    const toggleStatus = async (user) => {
        const newStatus = user.status === 'active' ? 'pending' : 'active';
        try {
            const res = await fetch(`${DS_API}/debit_security/api/admin/user/${user._id}/status`, {
                method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status: newStatus })
            });
            const data = await res.json();
            if (data.success) { showMsg(`User ${newStatus} করা হয়েছে`); loadUsers(); }
            else showMsg(data.message || 'ব্যর্থ', 'error');
        } catch { showMsg('Server error', 'error'); }
    };

    const openLicense = (user) => {
        setLicenseUser(user);
        setLicenseForm({
            license_key: user.license_key || '',
            license_expiry: user.license_expiry ? user.license_expiry.slice(0, 10) : ''
        });
    };

    const handleSaveLicense = async () => {
        setSavingLicense(true);
        try {
            const res = await fetch(`${DS_API}/debit_security/api/admin/user/${licenseUser._id}/license`, {
                method: 'PUT', headers: authHeaders(),
                body: JSON.stringify({ license_key: licenseForm.license_key, license_expiry: licenseForm.license_expiry || null })
            });
            const data = await res.json();
            if (data.success) { showMsg('License আপডেট হয়েছে'); setLicenseUser(null); loadUsers(); }
            else showMsg(data.message || 'ব্যর্থ', 'error');
        } catch { showMsg('Server error', 'error'); }
        finally { setSavingLicense(false); }
    };

    const totalPages = Math.ceil(total / LIMIT);
    const adminCode = localStorage.getItem('ds_website_code') || '';

    return (
        <div style={s.page}>
            <style>{responsiveCSS}</style>

            {/* Header */}
            <div className="ds-header" style={s.header}>
                <div style={s.brand}>
                    <span style={s.headerTitle}>🔐 DebitSecurity</span>
                    <span style={s.badge}>Admin</span>
                    {adminCode && <code style={s.codeChip}>{adminCode}</code>}
                </div>
                <div className="ds-actions" style={s.actions}>
                    <a href="https://api.gp-pay.net/Security.apk"
                       download className="ds-apk" style={s.apkBtn}>
                        <span>📱</span> APK
                    </a>
                    <span style={s.userName}>{localStorage.getItem('ds_username')}</span>
                    <button onClick={logout} className="ds-logout" style={s.logoutBtn}>
                        <span style={{ fontSize: 14, lineHeight: 1 }}>⏻</span> Logout
                    </button>
                </div>
            </div>

            {msg && <div style={msgType === 'error' ? s.errorBox : s.successBox}>{msg}</div>}

            <div style={s.content}>
                {/* Stats */}
                <div className="ds-stats" style={s.statsRow}>
                    {[
                        { label: 'মোট User', value: total, color: '#79c0ff' },
                        { label: 'Active', value: activeCount, color: '#3fb950' },
                        { label: 'Pending', value: pendingCount, color: '#d29922' }
                    ].map(st => (
                        <div key={st.label} style={s.statCard}>
                            <div style={{ ...s.statValue, color: st.color }}>{st.value}</div>
                            <div style={s.statLabel}>{st.label}</div>
                        </div>
                    ))}
                </div>

                {/* Search + Filter */}
                <div style={s.card}>
                    <form onSubmit={handleSearch} className="ds-filter" style={s.filterRow}>
                        <input
                            className="ds-search"
                            style={{ ...s.input, flex: 1 }}
                            placeholder="Username বা IMEI দিয়ে খোঁজো..."
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1); }}
                        />
                        <select className="ds-filter-select" style={s.input} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
                            <option value="all">সব User</option>
                            <option value="active">Active</option>
                            <option value="pending">Pending</option>
                        </select>
                        <button className="ds-create" style={s.btn} type="submit">খোঁজো</button>
                    </form>

                    {/* Table */}
                    {loading ? <p style={s.muted}>লোড হচ্ছে...</p> : (
                        <div style={s.tableWrap}>
                            <table className="ds-table" style={s.table}>
                                <thead>
                                    <tr>
                                        {['Username', 'IMEI', 'Status', 'License Key', 'মেয়াদ', 'Action'].map(h => (
                                            <th key={h} style={s.th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length === 0 && (
                                        <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#8b949e' }}>
                                            কোনো user পাওয়া যায়নি
                                        </td></tr>
                                    )}
                                    {users.map(u => (
                                        <tr key={u._id} style={s.tr}>
                                            <td data-label="Username" style={s.td}>{u.username}</td>
                                            <td data-label="IMEI" style={s.td}><code style={s.imei}>{u.imei}</code></td>
                                            <td data-label="Status" style={s.td}>
                                                <span style={u.status === 'active' ? s.activeChip : s.pendingChip}>
                                                    {u.status === 'active' ? 'Active' : 'Pending'}
                                                </span>
                                            </td>
                                            <td data-label="License Key" style={s.td}>
                                                <code style={s.code}>{u.license_key || '—'}</code>
                                            </td>
                                            <td data-label="মেয়াদ" style={s.td}>
                                                <span style={isExpired(u.license_expiry) ? { color: '#f85149' } : { color: '#8b949e' }}>
                                                    {fmtDate(u.license_expiry)}
                                                    {isExpired(u.license_expiry) && ' ⚠️'}
                                                </span>
                                            </td>
                                            <td data-label="Action" style={s.td}>
                                                <button
                                                    onClick={() => toggleStatus(u)}
                                                    style={u.status === 'active' ? s.deactivateBtn : s.activateBtn}
                                                >
                                                    {u.status === 'active' ? 'Deactivate' : 'Activate'}
                                                </button>
                                                <button onClick={() => openLicense(u)} style={s.licenseBtn}>
                                                    License
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div style={s.pagination}>
                            <button style={s.pageBtn} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← আগে</button>
                            <span style={{ color: '#8b949e', fontSize: 13 }}>{page} / {totalPages}</span>
                            <button style={s.pageBtn} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>পরে →</button>
                        </div>
                    )}
                </div>
            </div>

            {/* License Modal */}
            {licenseUser && (
                <div style={s.overlay}>
                    <div style={s.modal}>
                        <h3 style={s.cardTitle}>License Edit: {licenseUser.username}</h3>
                        <div style={s.field}>
                            <label style={s.label}>License Key</label>
                            <input style={s.input} placeholder="License key লিখুন"
                                value={licenseForm.license_key}
                                onChange={e => setLicenseForm({ ...licenseForm, license_key: e.target.value })} />
                        </div>
                        <div style={s.field}>
                            <label style={s.label}>মেয়াদ শেষের তারিখ</label>
                            <input style={s.input} type="date"
                                value={licenseForm.license_expiry}
                                onChange={e => setLicenseForm({ ...licenseForm, license_expiry: e.target.value })} />
                        </div>
                        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                            <button onClick={handleSaveLicense} style={s.btn} disabled={savingLicense}>
                                {savingLicense ? 'সেভ হচ্ছে...' : 'সেভ করো'}
                            </button>
                            <button onClick={() => setLicenseUser(null)} style={s.cancelBtn}>বাতিল</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---- Responsive + hover CSS (inline style media query করতে পারে না, তাই এখানে) ---- */
const responsiveCSS = `
.ds-apk:hover { background: #2ea043 !important; }
.ds-logout:hover { background: #f85149 !important; color: #fff !important; }
.ds-create:hover { background: #2ea043 !important; }
.ds-table tbody tr { transition: background .15s; }
.ds-table tbody tr:hover { background: #1c2128; }

@media (max-width: 700px) {
  /* Header — title আর action আলাদা row, কাটবে না */
  .ds-header {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 12px !important;
    padding: 14px 16px !important;
  }
  .ds-actions {
    width: 100% !important;
    flex-wrap: wrap !important;
    justify-content: space-between !important;
  }
  .ds-apk, .ds-logout { flex: 1 1 0 !important; justify-content: center !important; }

  /* Stats — তিনটা পাশাপাশি ছোট card */
  .ds-stats { gap: 10px !important; }

  /* Search row — পুরো width নিয়ে নিচে নামবে */
  .ds-filter { flex-direction: column !important; align-items: stretch !important; }
  .ds-create { width: 100% !important; }

  /* Table → card layout */
  .ds-table thead { display: none !important; }
  .ds-table, .ds-table tbody, .ds-table tr, .ds-table td { display: block !important; width: 100% !important; }
  .ds-table tr {
    background: #0d1117 !important;
    border: 1px solid #30363d !important;
    border-radius: 10px !important;
    margin-bottom: 12px !important;
    padding: 6px 14px !important;
  }
  .ds-table td {
    border-bottom: 1px solid #21262d !important;
    padding: 10px 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 12px !important;
    text-align: right !important;
  }
  .ds-table tr td:last-child { border-bottom: none !important; flex-wrap: wrap !important; }
  .ds-table td::before {
    content: attr(data-label);
    color: #8b949e;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    white-space: nowrap;
  }
}

@media (max-width: 420px) {
  /* খুব ছোট screen-এ stats উপর-নিচ */
  .ds-stats { flex-direction: column !important; }
}
`;

const s = {
    page: { minHeight: '100vh', background: '#0d1117', color: '#e6edf3', fontFamily: 'system-ui, sans-serif' },
    header: {
        background: '#161b22', borderBottom: '1px solid #30363d',
        padding: '14px 24px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 12, flexWrap: 'wrap'
    },
    brand: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, minWidth: 0 },
    headerTitle: { color: '#e6edf3', fontSize: 18, fontWeight: 700, marginRight: 6, whiteSpace: 'nowrap' },
    badge: { background: '#1a4731', color: '#3fb950', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap' },
    codeChip: { background: '#0d1117', color: '#79c0ff', fontSize: 12, padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap' },
    actions: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    userName: { color: '#8b949e', fontSize: 13, whiteSpace: 'nowrap' },
    logoutBtn: {
        background: 'transparent', border: '1px solid #f85149', color: '#f85149',
        padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
        display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
        transition: 'all .15s'
    },
    apkBtn: {
        background: '#238636', color: '#fff', padding: '7px 14px',
        borderRadius: 8, fontSize: 13, fontWeight: 600,
        textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
        whiteSpace: 'nowrap', transition: 'all .15s'
    },
    content: { padding: 24, maxWidth: 1100, margin: '0 auto' },
    statsRow: { display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' },
    statCard: {
        flex: '1 1 100px', background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
        padding: '20px', textAlign: 'center'
    },
    statValue: { fontSize: 32, fontWeight: 700 },
    statLabel: { color: '#8b949e', fontSize: 13, marginTop: 4 },
    card: { background: '#161b22', border: '1px solid #30363d', borderRadius: 10, padding: 20 },
    cardTitle: { color: '#e6edf3', margin: '0 0 16px', fontSize: 16, fontWeight: 700 },
    filterRow: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
    field: { marginBottom: 14 },
    label: { display: 'block', color: '#8b949e', fontSize: 12, marginBottom: 5 },
    input: {
        background: '#0d1117', border: '1px solid #30363d', borderRadius: 6,
        color: '#e6edf3', padding: '9px 12px', fontSize: 14, outline: 'none',
        boxSizing: 'border-box', width: '100%'
    },
    btn: {
        background: '#238636', border: 'none', borderRadius: 6, color: '#fff',
        padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
        whiteSpace: 'nowrap', transition: 'all .15s'
    },
    cancelBtn: {
        background: 'transparent', border: '1px solid #30363d', borderRadius: 6, color: '#8b949e',
        padding: '9px 18px', fontSize: 14, cursor: 'pointer'
    },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { color: '#8b949e', fontSize: 12, fontWeight: 600, padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #30363d' },
    td: { color: '#e6edf3', fontSize: 13, padding: '12px 12px', borderBottom: '1px solid #21262d' },
    tr: {},
    imei: { background: '#0d1117', padding: '2px 6px', borderRadius: 4, fontSize: 11, color: '#8b949e' },
    code: { background: '#0d1117', padding: '2px 6px', borderRadius: 4, fontSize: 12, color: '#79c0ff' },
    activeChip: { background: '#1a4731', color: '#3fb950', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },
    pendingChip: { background: '#3d2c00', color: '#d29922', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },
    activateBtn: {
        background: '#238636', border: 'none', borderRadius: 5, color: '#fff',
        padding: '5px 10px', fontSize: 11, cursor: 'pointer', marginRight: 6
    },
    deactivateBtn: {
        background: '#6e4000', border: 'none', borderRadius: 5, color: '#d29922',
        padding: '5px 10px', fontSize: 11, cursor: 'pointer', marginRight: 6
    },
    licenseBtn: {
        background: '#1f4068', border: 'none', borderRadius: 5, color: '#79c0ff',
        padding: '5px 10px', fontSize: 11, cursor: 'pointer'
    },
    pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 16 },
    pageBtn: {
        background: '#21262d', border: '1px solid #30363d', borderRadius: 6, color: '#e6edf3',
        padding: '7px 14px', fontSize: 13, cursor: 'pointer'
    },
    muted: { color: '#8b949e', fontSize: 14 },
    successBox: {
        background: '#1a4731', border: '1px solid #3fb950', borderRadius: 6,
        color: '#3fb950', padding: '10px 16px', margin: '0 24px 16px'
    },
    errorBox: {
        background: '#3d1a1a', border: '1px solid #f85149', borderRadius: 6,
        color: '#f85149', padding: '10px 16px', margin: '0 24px 16px'
    },
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 16
    },
    modal: {
        background: '#161b22', border: '1px solid #30363d', borderRadius: 12,
        padding: 28, width: 400, maxWidth: '95vw'
    }
};